import { KiziLogger } from '../components/Logger';
import { ActionContexts } from '../contexts/comparator/comparator-context';
import { FormField } from '../state-machines/form-machine';
import { GraphOSBusinessCarAPI } from '../services/comparator-api/graphql/api/os-business-car/graphOSBusinessCarAPI';
import { prettifyLicencePlate } from '../helpers/utils';

export interface ModuleFn {
  (contexts: ActionContexts): void;
}

export enum HookTypes {
  PAGE_LOADED = 'PAGE_LOADED',
}

export interface IHook {
  type: HookTypes;
  meta: object;
}

export interface PageLoadHook extends IHook {
  type: HookTypes.PAGE_LOADED;
  meta: {
    pageName: string;
  };
}

export type Hook = PageLoadHook; // in the future | AppLoadHook | ....
export type ModuleDef =
  | {
      on: Hook;
      module: ModuleFn;
      waitUntilLoaded?: boolean;
    }
  | {
      on?: Hook;
      module: ModuleFn;
      waitUntilLoaded: boolean;
    };

export type Module = ModuleFn | ModuleDef;

export const handleSpecialFieldCases = (fieldName, stepName, criteria) => {
  switch (fieldName) {
    case 'hometype':
      return criteria[fieldName][stepName];
    case 'cartype':
      return criteria['coverage'];
    default:
      return criteria[fieldName];
  }
};

export const getRawLicensePlate = (value?: string): string => {
  if (!value) return '';

  return value.replaceAll('-', '').toUpperCase();
};

export const comparisonId: ModuleDef = {
  waitUntilLoaded: true,
  module: (contexts) => {
    const triggerName = 'comparison-id';
    const searchParams = new URLSearchParams(location.search);

    if (searchParams.has(triggerName) && searchParams.get(triggerName)) {
      const comparisonPromise = contexts.comparator.api.getComparison(
        searchParams.get(triggerName) as string
      );

      comparisonPromise
        .then((comparison) => {
          if (contexts.wizard && contexts.comparator.navigate) {
            const steps = contexts.wizard.getSnapshot().context.initialSteps;

            let fields: FormField[] = [];
            for (const step of steps) {
              step.fields.forEach((f) => {
                const fieldName = f.name;
                const stepName = step.name;
                const { criteria } = comparison;
                const value = handleSpecialFieldCases(
                  fieldName,
                  stepName,
                  criteria
                );
                if (value !== undefined) {
                  fields.push({
                    name: fieldName,
                    value: value,
                    formName: stepName,
                  });
                }
              });
            }
            contexts.wizard.send([
              { type: 'SET_FIELDS', fields },
              'MODULE_LOADED:comparisonId' as any,
            ]);
            contexts.comparator.navigate({ path: 'overview' });
          }
        })
        .catch((e) => {
          KiziLogger.logError({
            eventId: 0,
            member: 'core',
            message: 'Error in comparisonId module',
            eventName: 'comparisonIdError',
            data: {
              error: e,
            },
          });
          contexts.wizard?.send('MODULE_LOADED:comparisonId' as any);
          if (contexts.comparator.navigate) {
            contexts.comparator.navigate({ path: 'funnel' });
          }
        });
    } else {
      contexts.wizard?.send('MODULE_LOADED:comparisonId' as any);
    }
  },
};

export const prefill: ModuleDef = {
  waitUntilLoaded: true,
  module: async (contexts) => {
    const cbTriggerName = 'name';
    const osTriggerName = 'osName';
    const expectedValue = 'prefill';
    const searchParams = new URLSearchParams(location.search);
    const isOsBusinessCar = contexts.comparator.name === 'os-business-car';
    if (
      searchParams.get(cbTriggerName) === expectedValue ||
      searchParams.get(osTriggerName) === expectedValue
    ) {
      try {
        const widgetInputsRaw = localStorage.getItem('comparatorWidget');
        if (!widgetInputsRaw) {
          contexts.wizard?.send('MODULE_LOADED:prefill' as any);
          return;
        }

        const widgetInputs = JSON.parse(decodeURIComponent(widgetInputsRaw));
        if (!widgetInputs) {
          contexts.wizard?.send('MODULE_LOADED:prefill' as any);
          return;
        }

        if (widgetInputs['licensePlate']) {
          if (isOsBusinessCar) {
            widgetInputs['licensePlate'] = prettifyLicencePlate(widgetInputs['licensePlate']);
            (contexts.comparator.api as GraphOSBusinessCarAPI).getCarData(widgetInputs['licensePlate']);
          } else {
            const rawLicensePlate = getRawLicensePlate(
              widgetInputs['licensePlate']
            );

            const response = await fetch(
              `${process.env.KIZI_PUBLIC_API_BASE_URL}/cars/vehicles/${rawLicensePlate}`,
              {
                headers: {
                  Authorization:
                    'Basic ' + process.env.KIZI_PUBLIC_API_AUTH_TOKEN,
                },
              }
            );

            const responseJson = await response.json();

            widgetInputs['carInfoResponse'] = JSON.stringify(responseJson.data);
          }
        }

        if (widgetInputs && contexts.wizard && contexts.comparator.navigate) {
          const steps = contexts.wizard.getSnapshot().context.initialSteps;
          let fields: FormField[] = [];

          // Helper function to find which step defines a field
          const findStepForField = (fieldName: string) => {
            for (const step of steps) {
              if (step.fields.some((f) => f.name === fieldName)) {
                return step.name;
              }
            }
            console.log(fieldName, '- FIELD_DOES_NOT_EXIST_OR_IS_INCORRECT');
            return 'FIELD_DOES_NOT_EXIST_OR_IS_INCORRECT';
          };

          Object.keys(widgetInputs).forEach((key) => {
            const correctStepName = findStepForField(key);
            const value = Array.isArray(widgetInputs[key])
              ? JSON.stringify(widgetInputs[key])
              : widgetInputs[key];
            fields.push({
              name: key,
              value: value,
              formName: correctStepName,
            });
          });

          // Check if localStorage state is properly initialized
          const localStorageKey = `kizi-comparator-${contexts.comparator.name}`;
          let isStateInitialized = false;

          try {
            const existingState = localStorage.getItem(localStorageKey);
            isStateInitialized =
              !!existingState && existingState !== 'undefined';
          } catch (error) {
            console.error('Error checking localStorage state:', error);
          }

          // If state is not initialized, we might need to ensure it gets initialized
          if (!isStateInitialized) {
            // Try to get state from context and persist it
            if (
              contexts.wizard &&
              contexts.comparator.persistStateInLocalStorage
            ) {
              const currentState = contexts.wizard.getSnapshot();
              if (currentState) {
                contexts.comparator.persistStateInLocalStorage(currentState);
              }
            }
          }

          console.log(fields);
          contexts.wizard.send([
            { type: 'SET_FIELDS', fields },
            'MODULE_LOADED:prefill' as any,
          ]);
        }
      } catch (error) {
        console.error('Error parsing widgetInputs from localStorage', error);
      }
    } else {
      contexts.wizard?.send('MODULE_LOADED:prefill' as any);
    }
  },
};

export const dynamicEnergyFilter: ModuleDef = {
  waitUntilLoaded: true,
  module: (contexts) => {
    const triggerName = 'name';
    const expectedValue = 'dynamic';
    const searchParams = new URLSearchParams(location.search);

    if (searchParams.get(triggerName) === expectedValue) {
      try {
        localStorage.setItem('dynamicEnergyFilter', 'true');
        if (contexts.wizard && contexts.comparator.navigate) {
          contexts.comparator.navigate({ path: 'funnel' }, { reload: true });
        }
      } catch (error) {
        console.error('Error: ', error);
      }
    } else {
      contexts.wizard?.send('MODULE_LOADED:dynamicEnergyFilter' as any);
    }
  },
};

export const sortingFilter: ModuleDef = {
  waitUntilLoaded: true,
  module: (contexts) => {
    const sortingMap = {
      aanbevolen: '0',
      laagstejaarprijs: '1',
      laagstemaandprijs: '2',
    };
    const triggerName = 'sorting';
    const searchParams = new URLSearchParams(location.search);
    const sortingOption = searchParams.get(triggerName);

    if (sortingOption !== null) {
      try {
        sessionStorage.setItem('sorting', sortingMap[sortingOption]);
        contexts.wizard?.send('MODULE_LOADED:sortingFilter' as any);
      } catch (error) {
        console.error('Error: ', error);
      }
    } else {
      contexts.wizard?.send('MODULE_LOADED:sortingFilter' as any);
    }
  },
};

export const gclid: ModuleDef = {
  waitUntilLoaded: true,
  module: (contexts) => {
    const triggerName = 'gclid';
    const searchParams = new URLSearchParams(location.search);

    if (searchParams.has(triggerName) && searchParams.get(triggerName)) {
      localStorage.setItem('gclid', searchParams.get(triggerName) as string);
      localStorage.setItem(
        'gclidExpiryPeriod',
        (new Date().getTime() + 90 * 24 * 60 * 60 * 1000).toString()
      );
    }
    contexts.wizard?.send('MODULE_LOADED:gclid' as any);
  },
};

export const productKrn: ModuleDef = {
  waitUntilLoaded: true,
  module: (contexts) => {
    const triggerName = 'product-krn';
    const searchParams = new URLSearchParams(location.search);

    if (searchParams.has(triggerName) && searchParams.get(triggerName)) {
      const productKrn = searchParams.get(triggerName) || '';
      const deductibleAmount = searchParams.get('deductibleAmount') || '385';

      if (!deductibleAmount) {
        contexts.wizard?.send('MODULE_LOADED:productKrn' as any);
      }

      const calculationData = {
        deductibleAmount: Number(deductibleAmount),
        birthDate: '01-01-1980',
        postalCode: '1054HL',
        gender: 'male',
        partner: {
          birthDate: undefined,
          gender: undefined,
        },
        children: [],
        coverages: [],
        customProperties: {
          customAttributes: [
            {
              description: 'Bundle',
              value: 'true',
            },
            {
              description: 'FullZorgWebCode',
              value: productKrn
                .substring(productKrn.indexOf('zorgweb/') + 8)
                .toUpperCase(),
            },
          ],
        },
      };

      contexts.comparator.api
        .getProducts(calculationData, {}, undefined, undefined, false)
        .then((product) => {
          const fields = [
            {
              name: 'postcode',
              value: '1054HL',
              formName: 'getUserInput',
            },
            {
              name: 'birthdate',
              value: '01-01-1980',
              formName: 'getUserInput',
            },
            {
              name: 'gender',
              value: 'male',
              formName: 'getUserInput',
            },
            {
              name: 'flow',
              value: 'direct',
              formName: 'getUserInput',
            },
            {
              name: 'deductibleAmount',
              value: deductibleAmount,
              formName: 'getUserInput',
            },
            {
              name: 'clientName',
              value: product[0].commonTechnical[0].accessCode,
              formName: 'getUserInput',
            },
            {
              name: 'requestLogId',
              value: product[0].commonTechnical[0].transactionReference,
              formName: 'overview',
            },
            {
              name: 'selectedProduct',
              value: JSON.stringify({ ...product[0] }),
              formName: 'overview',
            },
          ];

          if (contexts.wizard && contexts.comparator.navigate) {
            contexts.wizard.send([
              { type: 'SET_FIELDS', fields },
              'MODULE_LOADED:productKrn' as any,
            ]);
            contexts.comparator.navigate({ path: 'summary' });
          } else {
            contexts.wizard?.send('MODULE_LOADED:productKrn' as any);
          }
        })
        .catch((err) => {
          console.log(err);
        });
    } else {
      contexts.wizard?.send('MODULE_LOADED:productKrn' as any);
    }
  },
};

export const defaultModules = [
  gclid,
  prefill,
  productKrn,
  comparisonId,
  dynamicEnergyFilter,
  sortingFilter,
];
